import {
  AdvertisementStatusEnum,
  SalesPlatformsResBody,
  useGetSalesPlatformsQuery,
} from '@dms/api/sales';
import {isAdvertisementPlatformEnabled} from '@dms/shared';

import {useAdvertisementPolling} from '../contexts/AdvertisementPollingContext';

interface UseAdvertisementPlatformFilterProps {
  vehicleId: string;
  skipPolling?: boolean;
}

interface PlatformInfo {
  code: string;
  enabled: boolean;
}

/**
 * Hook for filtering advertisement platforms based on feature flags and platform status
 * @param props - Configuration object containing vehicleId and optional skipPolling flag
 * @returns Object containing filtered platforms data and loading state
 */
export function useAdvertisementPlatformFilter(props: UseAdvertisementPlatformFilterProps) {
  const {currentPollingInterval} = useAdvertisementPolling();

  const {
    data: platforms,
    isLoading,
    isError,
  } = useGetSalesPlatformsQuery(
    {vehicleId: props.vehicleId},
    {
      pollingInterval: props.skipPolling ? 0 : currentPollingInterval,
      skipPollingIfUnfocused: true,
    }
  );

  const isPlatformEnabled = (platform: PlatformInfo): boolean => {
    if (!platform.enabled) {
      return false;
    }

    return isAdvertisementPlatformEnabled(platform);
  };

  const shouldIncludePlatform = (platformData: SalesPlatformsResBody): boolean => {
    const platformEnabled = isPlatformEnabled(platformData.platform);
    const statusPublished = platformData.status !== AdvertisementStatusEnum.NOT_PUBLISHED;

    return platformEnabled || statusPublished;
  };

  const filteredPlatforms = platforms?.filter(shouldIncludePlatform) ?? [];

  return {
    platforms: filteredPlatforms,
    isLoading,
    isError,
    allPlatforms: platforms,
    isPlatformEnabled,
  };
}
